<template>
    <PageHeader :crumbs="[{ label: $t('dashboard.sidebar.dashboard') }]" />

    <div class="tw-p-2 tw-space-y-4">
        <!-- Welcome -->
        <div class="tw-bg-white tw-rounded tw-border tw-border-b-2 tw-border-solid tw-p-5">
            <p class="tw-text-sm tw-text-gray-400">{{ $t('portal.dashboard.welcome') }}</p>
            <h1 class="tw-text-2xl tw-font-bold tw-text-gray-800">{{ customer.name }}</h1>
        </div>

        <!-- Active products (primary widget) -->
        <div class="tw-bg-white tw-rounded tw-border tw-border-b-2 tw-border-solid tw-p-4">
            <h3 class="tw-font-semibold tw-text-gray-700 tw-mb-3">{{ $t('portal.dashboard.active-products') }}</h3>
            <DataTable :columns="activeColumns" :count="activeProducts.length">
                <tr v-for="p in activeProducts" :key="p.id" :class="rowClass">
                    <td :class="cellClass"><span class="tw-font-medium tw-text-gray-800">{{ p.product_name }}</span></td>
                    <td :class="cellClass"><span class="tw-capitalize">{{ p.product_type }}</span></td>
                    <td :class="cellClass">{{ p.expiry_date ? fmt.date(p.expiry_date) : $t('common.without') }}</td>
                    <td :class="cellClass"><StatusBadge :status="p.status" /></td>
                </tr>
            </DataTable>
        </div>

        <!-- Stats (colorful) -->
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
            <div v-for="card in cards" :key="card.key"
                class="tw-relative tw-overflow-hidden tw-rounded-xl tw-p-4 tw-text-white tw-shadow-lg tw-bg-gradient-to-br"
                :class="card.gradient">
                <icon :icon="card.icon" class="tw-absolute tw-right-2 tw-top-2 tw-text-6xl tw-opacity-20" />
                <div class="tw-relative">
                    <div class="tw-p-2 tw-w-fit tw-rounded-lg tw-bg-white/20 tw-mb-3">
                        <icon :icon="card.icon" class="tw-text-xl" />
                    </div>
                    <p class="tw-text-xs tw-uppercase tw-font-semibold tw-text-white/80">{{ card.label }}</p>
                    <p class="tw-text-2xl tw-font-bold">{{ card.value }}</p>
                </div>
            </div>
        </div>

        <!-- Unread notifications widget -->
        <div class="tw-bg-white tw-rounded tw-border tw-border-b-2 tw-border-solid tw-p-4">
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
                <h3 class="tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                    <icon icon="solar:bell-bing-bold-duotone" class="tw-text-primary-500 tw-text-xl" />
                    {{ $t('portal.dashboard.unread-notifications') }}
                    <span v-if="unreadNotifications.length" class="tw-text-xs tw-bg-primary-500 tw-text-white tw-rounded-full tw-px-2 tw-py-0.5">{{ stats.unread_notifications }}</span>
                </h3>
                <router-link :to="{ name: 'portal.notifications' }" class="tw-text-xs tw-font-semibold tw-text-primary-500 hover:tw-text-primary-600 tw-inline-flex tw-items-center tw-gap-1">
                    {{ $t('portal.dashboard.show-more') }}
                    <icon icon="solar:arrow-right-linear" />
                </router-link>
            </div>

            <ul class="tw-divide-y">
                <li v-for="n in unreadNotifications" :key="n.id"
                    class="tw-py-2.5 tw-flex tw-items-start tw-gap-3 tw-cursor-pointer hover:tw-bg-gray-50 tw-rounded tw-px-1"
                    @click="openNotifications">
                    <span class="tw-mt-1 tw-w-2 tw-h-2 tw-shrink-0 tw-rounded-full tw-bg-primary-500"></span>
                    <div class="tw-min-w-0">
                        <p class="tw-text-sm tw-font-medium tw-text-gray-800 tw-truncate">{{ n.title }}</p>
                        <p class="tw-text-xs tw-text-gray-500 tw-truncate">{{ n.message }}</p>
                    </div>
                    <span class="tw-ml-auto tw-text-[11px] tw-text-gray-400 tw-whitespace-nowrap">{{ fmt.datetime(n.date) }}</span>
                </li>
                <li v-if="!unreadNotifications.length" class="tw-py-6 tw-text-center tw-text-sm tw-text-gray-400">
                    {{ $t('portal.dashboard.no-unread') }}
                </li>
            </ul>
        </div>

        <!-- Support tickets widget -->
        <div class="tw-bg-white tw-rounded tw-border tw-border-b-2 tw-border-solid tw-p-4">
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
                <h3 class="tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                    <icon icon="solar:chat-round-line-bold-duotone" class="tw-text-primary-500 tw-text-xl" />
                    {{ $t('portal.tickets.my-tickets') }}
                    <span v-if="stats.unread_tickets > 0" class="tw-text-xs tw-bg-primary-500 tw-text-white tw-rounded-full tw-px-2 tw-py-0.5">{{ stats.unread_tickets }} {{ $t('portal.tickets.new-reply') }}</span>
                </h3>
                <router-link :to="{ name: 'portal.tickets' }" class="tw-text-xs tw-font-semibold tw-text-primary-500 hover:tw-text-primary-600 tw-inline-flex tw-items-center tw-gap-1">
                    {{ $t('portal.dashboard.show-more') }}
                    <icon icon="solar:arrow-right-linear" />
                </router-link>
            </div>

            <ul class="tw-divide-y">
                <li v-for="tk in recentTickets" :key="tk.id"
                    class="tw-py-2.5 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-cursor-pointer hover:tw-bg-gray-50 tw-rounded tw-px-1"
                    @click="openTicket(tk)">
                    <div class="tw-min-w-0 tw-flex tw-items-center tw-gap-2">
                        <span v-if="tk.is_unread" class="tw-w-2 tw-h-2 tw-shrink-0 tw-rounded-full tw-bg-primary-500"></span>
                        <div class="tw-min-w-0">
                            <p class="tw-text-sm tw-font-medium tw-text-gray-800 tw-truncate" :class="{ 'tw-font-semibold': tk.is_unread }">{{ tk.subject }}</p>
                            <p class="tw-text-xs tw-text-gray-400">{{ tk.messages_count }} {{ $t('portal.tickets.messages') }} · {{ fmt.datetime(tk.last_reply_at) }}</p>
                        </div>
                    </div>
                    <TicketStatusBadge :status="tk.status" />
                </li>
                <li v-if="!recentTickets.length" class="tw-py-6 tw-text-center tw-text-sm tw-text-gray-400">
                    {{ $t('portal.tickets.none') }}
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { dashboard as fetchDashboard } from '@/api/portal';
import PageHeader from '@/components/PageHeader.vue';
import StatusBadge from '@/components/StatusBadge.vue';
import TicketStatusBadge from '@/views/admin/tickets/TicketStatusBadge.vue';
import DataTable from '@/components/table/DataTable.vue';
import { useFormat } from '@/composables/useFormat';
import { rowClass, cellClass } from '@/composables/useTableStyles';
import { usePortalStore } from '@/stores';

const { t } = useI18n();
const fmt = useFormat();
const router = useRouter();
const portalStore = usePortalStore();

const activeColumns = computed(() => [
    { label: t('fields.product') },
    { label: t('portal.fields.type'), classes: ['tw-w-[150px]'] },
    { label: t('portal.fields.expiry-date'), classes: ['tw-w-[160px]'] },
    { label: t('fields.status'), classes: ['tw-w-[120px]'] },
]);

const customer = ref({ name: '' });
const stats = ref({ active_products: 0, expiring_soon: 0, unread_notifications: 0, unread_tickets: 0 });
const activeProducts = ref([]);
const unreadNotifications = ref([]);
const recentTickets = ref([]);

const openNotifications = () => router.push({ name: 'portal.notifications' });
const openTicket = (tk) => router.push({ name: 'portal.tickets.show', params: { id: tk.id } });

const cards = computed(() => [
    { key: 'active', label: t('portal.dashboard.active-products'), value: stats.value.active_products, icon: 'solar:box-bold-duotone', gradient: 'tw-from-primary-500 tw-to-primary-700' },
    { key: 'expiring', label: t('portal.dashboard.expiring-soon'), value: stats.value.expiring_soon, icon: 'solar:clock-circle-bold-duotone', gradient: 'tw-from-amber-500 tw-to-orange-600' },
    { key: 'unread', label: t('portal.dashboard.unread-notifications'), value: stats.value.unread_notifications, icon: 'solar:bell-bing-bold-duotone', gradient: 'tw-from-cyan-500 tw-to-blue-600' },
]);

async function load() {
    try {
        const { data } = await fetchDashboard();
        customer.value = data.customer;
        stats.value = data.stats;
        activeProducts.value = data.active_products || [];
        unreadNotifications.value = data.unread_notifications || [];
        recentTickets.value = data.recent_tickets || [];
        // Keep the sidebar badges in sync.
        portalStore.setUnread(data.stats?.unread_notifications || 0);
        portalStore.setUnreadTickets(data.stats?.unread_tickets || 0);
    } catch (e) {
        // handled by interceptor
    }
}

load();
</script>
